import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import { tr } from 'date-fns/locale/tr';
import 'react-datepicker/dist/react-datepicker.css';
import { Modal } from '@/components';
import { STUDENT_AVATAR_TONE_COUNT } from '../../boutique-classroom.constants';
import { PlannedLessonDraft, PlannedLessonSlot } from '../../boutique-classroom.types';
import { BoutiqueStudent } from '../../students/boutique-students.types';
import { hasStudentConflict, useExpertPlannedSlots, useStudentPlannedLessons } from '../../hooks';
import { PLANNED_LESSON_DATE_PARAM_FORMAT, PLANNED_LESSON_ERROR, PLANNED_LESSON_MONTH_FORMAT, PLANNED_LESSON_TEXT, PLANNED_LESSON_WEEKDAY_SHORT } from './planned-lesson-modal.constants';
import { formatDate, getInitials, toSlotKey } from './planned-lesson-modal.utils';

interface PlannedLessonDateModalProps {
	student: BoutiqueStudent | null;
	subtitle: string;
	draft: PlannedLessonDraft | null;
	takenSlotKeys: string[];
	onClose: () => void;
	onSelect: (date: string, slot: PlannedLessonSlot) => void;
}

export const PlannedLessonDateModal: React.FC<PlannedLessonDateModalProps> = ({ student, subtitle, draft, takenSlotKeys, onClose, onSelect }) => {
	const [date, setDate] = useState<string | null>(null);
	const [slot, setSlot] = useState<PlannedLessonSlot | null>(null);
	const isOpen = student !== null;

	useEffect(() => {
		if (!isOpen) return;
		setDate(draft?.date ?? null);
		setSlot(draft?.slot ?? null);
	}, [draft, isOpen]);

	const { slotsByDate, dayRange, isLoading } = useExpertPlannedSlots({ studentId: student?.id, branchId: draft?.branchId, expertId: draft?.expertId });
	const studentLessons = useStudentPlannedLessons(student?.id, date);
	const daySlots = date ? (slotsByDate[date] ?? []) : [];
	const conflict = useMemo(
		() => (date && hasStudentConflict(studentLessons, [draft?.lesson?.id, draft?.topic?.id]) ? PLANNED_LESSON_ERROR.STUDENT_CONFLICT : null),
		[date, draft?.lesson?.id, draft?.topic?.id, studentLessons]
	);

	const handleDateChange = (value: Date | null) => {
		setDate(value ? dayjs(value).format(PLANNED_LESSON_DATE_PARAM_FORMAT) : null);
		setSlot(null);
	};

	const handleSubmit = () => {
		if (date && slot && !conflict) onSelect(date, slot);
	};

	const renderSlots = () => {
		if (isLoading) return <p className="plm-date__hint">{PLANNED_LESSON_TEXT.SLOTS_LOADING}</p>;
		if (!date) return <p className="plm-date__hint">{PLANNED_LESSON_TEXT.PICK_DAY_FIRST}</p>;
		if (!daySlots.length) return <p className="plm-date__hint">{PLANNED_LESSON_TEXT.NO_SLOTS_FOR_DAY}</p>;
		return (
			<div className="plm-date__slots">
				{daySlots.map(item => (
					<button
						key={item.id}
						type="button"
						className={clsx('plm-date__slot', { 'plm-date__slot--active': slot?.id === item.id })}
						disabled={takenSlotKeys.includes(toSlotKey(date, item.id))}
						onClick={() => setSlot(item)}>
						{item.startTime}
					</button>
				))}
			</div>
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			hasHeader
			title={PLANNED_LESSON_TEXT.DATE_MODAL_TITLE}
			showCloseButton
			size="lg"
			variant="centered"
			closeOnClickOutside={false}
			className="cgm-modal plm-modal plm-modal--date"
			contentClassName="cgm-modal__content"
			headerClassName="cgm-modal__header"
			bodyClassName="cgm-modal__body">
			{student && (
				<div className="cgm">
					<div className="cgm__scroll">
						<div className="cgm-student">
							<span className={clsx('cgm-student__avatar', `cgm-student__avatar--tone-${student.id % STUDENT_AVATAR_TONE_COUNT}`)}>{getInitials(student.fullName)}</span>
							<span className="cgm-student__text">
								<span className="cgm-student__name">{student.fullName}</span>
								<span className="cgm-student__meta">{subtitle}</span>
							</span>
						</div>

						<div className="plm-date">
							<div className="plm-date__calendar">
								<DatePicker
									inline
									fixedHeight
									locale={tr}
									selected={date ? dayjs(date).toDate() : null}
									onChange={handleDateChange}
									minDate={new Date()}
									maxDate={dayjs().add(dayRange, 'day').toDate()}
									filterDate={value => Boolean(slotsByDate[dayjs(value).format(PLANNED_LESSON_DATE_PARAM_FORMAT)])}
									formatWeekDay={dayName => PLANNED_LESSON_WEEKDAY_SHORT[dayName.toLocaleLowerCase('tr-TR')] ?? dayName}
									renderCustomHeader={({ date: monthDate, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
										<div className="plm-date__month">
											<button
												type="button"
												className="plm-date__nav"
												aria-label={PLANNED_LESSON_TEXT.PREV_MONTH}
												disabled={prevMonthButtonDisabled}
												onClick={decreaseMonth}>
												<i className="icons-alt-arrow-left" />
											</button>
											<span>{formatDate(dayjs(monthDate).format(PLANNED_LESSON_DATE_PARAM_FORMAT), PLANNED_LESSON_MONTH_FORMAT)}</span>
											<button
												type="button"
												className="plm-date__nav"
												aria-label={PLANNED_LESSON_TEXT.NEXT_MONTH}
												disabled={nextMonthButtonDisabled}
												onClick={increaseMonth}>
												<i className="icons-alt-arrow-right" />
											</button>
										</div>
									)}
								/>
							</div>
							<div className="plm-date__times">
								<span className="cgm-field__label">{PLANNED_LESSON_TEXT.START_TIME_LABEL}</span>
								<div className="plm-date__slot-area">{renderSlots()}</div>
							</div>
						</div>

						{conflict && (
							<div className="cgm-info plm-error">
								<i className="icons-danger-circle" />
								<span>
									<strong>{conflict.title}</strong>
									<br />
									{conflict.message}
								</span>
							</div>
						)}
					</div>

					<div className="cgm__footer">
						<button
							type="button"
							className="cgm-button cgm-button--secondary"
							onClick={onClose}>
							{PLANNED_LESSON_TEXT.CANCEL}
						</button>
						<button
							type="button"
							className="cgm-button cgm-button--primary"
							disabled={!date || !slot || Boolean(conflict)}
							onClick={handleSubmit}>
							{PLANNED_LESSON_TEXT.SELECT}
						</button>
					</div>
				</div>
			)}
		</Modal>
	);
};

export default PlannedLessonDateModal;
